import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  ButtonGroup,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "react-simple-snackbar";
import { getInvoicesByUser, updateInvoice } from "../../actions/invoiceActions";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "No date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "No date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const collectFlaggedItems = (items = []) =>
  items.filter((item = {}) => {
    const unitCost = parseFloat(item?.unitCost) || 0;
    const unitPrice = parseFloat(item?.unitPrice) || 0;
    return unitCost > unitPrice;
  });

export default function FlaggedInvoicesCard({
  title = "Flagged Invoices",
  currency = "USD",
  autoLoad = true,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [openSnackbar] = useSnackbar();
  const { invoices = [], isLoading = false } = useSelector(
    (state) => state?.invoices || {}
  );
  const [statusFilter, setStatusFilter] = React.useState("unresolved");
  const [updatingById, setUpdatingById] = React.useState({});

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (!autoLoad) return;
    const id = userInfo?._id || userInfo?.googleId;
    if (!id) return;
    if (invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [autoLoad, dispatch, invoices?.length, userInfo]);

  const flaggedInvoices = React.useMemo(() => {
    return (invoices || [])
      .map((inv) => {
        const flaggedItems = collectFlaggedItems(inv?.items);
        return flaggedItems.length
          ? {
              ...inv,
              flaggedItems,
              isResolved: Boolean(inv?.flaggedResolved),
            }
          : null;
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          new Date(b?.receivedByDate || b?.createdAt || 0) -
          new Date(a?.receivedByDate || a?.createdAt || 0)
      );
  }, [invoices]);

  const visibleFlaggedInvoices = React.useMemo(() => {
    if (statusFilter === "resolved") return flaggedInvoices.filter((inv) => inv.isResolved);
    if (statusFilter === "all") return flaggedInvoices;
    return flaggedInvoices.filter((inv) => !inv.isResolved);
  }, [flaggedInvoices, statusFilter]);

  const unresolvedCount = React.useMemo(
    () => flaggedInvoices.filter((inv) => !inv.isResolved).length,
    [flaggedInvoices]
  );
  const resolvedCount = React.useMemo(
    () => flaggedInvoices.filter((inv) => inv.isResolved).length,
    [flaggedInvoices]
  );

  const handleToggleResolved = async (event, inv, nextResolved) => {
    event.stopPropagation();
    if (!inv?._id || updatingById[inv._id]) return;
    const userId = userInfo?._id || userInfo?.googleId || null;
    const { flaggedItems, isResolved, ...baseInvoice } = inv;
    const payload = {
      ...baseInvoice,
      flaggedResolved: nextResolved,
      flaggedResolvedAt: nextResolved ? new Date().toISOString() : null,
      flaggedResolvedBy: nextResolved ? userId : null,
    };
    setUpdatingById((prev) => ({ ...prev, [inv._id]: true }));
    try {
      await dispatch(updateInvoice(inv._id, payload));
      openSnackbar(nextResolved ? "Flagged invoice marked resolved." : "Flagged invoice marked unresolved.");
    } catch (error) {
      openSnackbar("Unable to update flagged invoice status.");
    } finally {
      setUpdatingById((prev) => {
        const next = { ...prev };
        delete next[inv._id];
        return next;
      });
    }
  };

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader
        title={title}
        subheader={
          flaggedInvoices.length
            ? `${visibleFlaggedInvoices.length} shown • ${flaggedInvoices.length} flagged ${
                flaggedInvoices.length === 1 ? "invoice" : "invoices"
              }`
            : undefined
        }
        action={
          <ButtonGroup size="small" variant="outlined" aria-label="flagged invoice filter">
            <Button
              variant={statusFilter === "unresolved" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("unresolved")}
            >
              Unresolved ({unresolvedCount})
            </Button>
            <Button
              variant={statusFilter === "resolved" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("resolved")}
            >
              Resolved ({resolvedCount})
            </Button>
            <Button
              variant={statusFilter === "all" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("all")}
            >
              All ({flaggedInvoices.length})
            </Button>
          </ButtonGroup>
        }
      />
      <CardContent style={{ maxHeight: 320, overflowY: "auto" }}>
        {isLoading ? (
          <Typography variant="body2" color="textSecondary">
            Loading invoices...
          </Typography>
        ) : visibleFlaggedInvoices.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {flaggedInvoices.length === 0
              ? "No invoices are currently flagged."
              : "No invoices match the selected filter."}
          </Typography>
        ) : (
          visibleFlaggedInvoices.map((inv) => (
            <Box
              key={inv._id}
              display="flex"
              flexDirection="column"
              py={0.75}
              borderBottom="1px solid rgba(0,0,0,0.06)"
              style={{ cursor: "pointer" }}
              onClick={() => history.push(`/invoice/${inv._id}`)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {inv?.client?.store || inv?.client?.name || "Unknown client"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                    Received Date: {formatDate(inv.receivedByDate || inv.createdAt)}
                  </Typography>
                  {inv.dueDate ? (
                    <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                      Due Date: {formatDate(inv.dueDate)}
                    </Typography>
                  ) : null}
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {currencyFmt(inv.total, currency)}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${inv.flaggedItems.length} flagged item${
                      inv.flaggedItems.length === 1 ? "" : "s"
                    }`}
                    style={{
                      marginTop: 4,
                      backgroundColor: "#dc2626",
                      color: "#fff",
                    }}
                  />
                  <Chip
                    size="small"
                    label={inv.isResolved ? "Resolved" : "Unresolved"}
                    style={{
                      marginTop: 4,
                      marginLeft: 4,
                      backgroundColor: inv.isResolved ? "#16a34a" : "#6b7280",
                      color: "#fff",
                    }}
                  />
                  <Box display="flex" alignItems="center" justifyContent="flex-end" mt={0.5} style={{ gap: 4 }}>
                    <Typography variant="caption" color="primary">
                      View Invoice
                    </Typography>
                    <IconButton size="small" style={{ padding: 0 }}>
                      <LaunchIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Box>
                  <Box mt={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={inv.isResolved ? "default" : "primary"}
                      disabled={Boolean(updatingById[inv._id])}
                      onClick={(event) => handleToggleResolved(event, inv, !inv.isResolved)}
                      style={{ textTransform: "none" }}
                    >
                      {updatingById[inv._id]
                        ? "Updating..."
                        : inv.isResolved
                          ? "Mark Unresolved"
                          : "Mark Resolved"}
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Box mt={0.5} pl={0.5}>
                <Typography variant="caption" color="error" style={{ fontWeight: 500 }}>
                  Below margin line items:
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  {inv.flaggedItems
                    .map(
                      (item) =>
                        item?.modelNumber ||
                        item?.description ||
                        item?.productName ||
                        "Unnamed item"
                    )
                    .join(", ")}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
